import React, { useMemo } from "react";
import type { DateValue } from "react-aria";
import { ListBox, ListBoxItem } from "react-aria-components";
import type { DateRangePickerState, Selection } from "react-stately";
import { colors } from "../Theme";
import { css } from "@linaria/core";
import { StyledListBoxItem } from "../TimeField/TimeSelector";
import Text from "../Text";
import classNames from "classnames";
import type { RangeValue } from "@react-types/shared";
import type { MappedDateValue } from "@react-types/datepicker";

const PresetsWrapper = css`
  display: flex;
  flex-direction: column;
  border-left: 1px solid ${colors.titanium100};
  padding: 12px;
  gap: 2px;
  width: 100%;
`;

const ItemClassname = css`
  min-width: 95px;
`;

export interface Preset<T extends DateValue, PresetId extends string = string> {
  label: string;
  startDate: T | (() => T);
  endDate: T | (() => T);
  metadata?: { id: PresetId } & { [key: string]: unknown };
}

const getPresetValues = <T extends DateValue, Id extends string>(preset: Preset<T, Id>) => ({
  start: preset.startDate instanceof Function ? preset.startDate() : preset.startDate,
  end: preset.endDate instanceof Function ? preset.endDate() : preset.endDate,
});

const PresetSelector = <T extends DateValue, Id extends string>({
  presets,
  state,
  onSelectionChange,
  manualValidation,
  tempRangeValue,
}: {
  presets: Preset<T, Id>[];
  state: DateRangePickerState;
  onSelectionChange?: (preset?: Preset<T, Id>, start?: T, end?: T) => void;
  manualValidation?: boolean;
  tempRangeValue?: RangeValue<MappedDateValue<T>> | null;
}) => {
  const listRef = React.useRef<HTMLDivElement>(null);

  const options = React.useMemo(
    () =>
      presets.map(e => ({
        ...presets,
        id: e.label,
        name: e.label,
      })),
    [presets],
  );

  const selected = useMemo(() => {
    if (!state.value || !state.value.start || !state.value.end) return [];
    const preset = presets.find(p => {
      const { start, end } = getPresetValues(p);
      const target = manualValidation && tempRangeValue ? tempRangeValue : state.value;
      if (!target?.start || !target?.end) return false;

      const sameStart =
        target.start
          ?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
          .compare(start.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })) === 0;
      /** @note be loose on end preset matching */
      const sameEnd =
        target.end
          ?.set({ millisecond: 999, hour: 23, minute: 59, second: 59 })
          .compare(end.set({ millisecond: 999, hour: 23, minute: 59, second: 59 })) === 0;

      const inRange = sameStart && sameEnd;
      return inRange;
    });
    return preset ? [preset?.label] : [];
  }, [manualValidation, presets, state.value, tempRangeValue]);

  const onSelectPreset = React.useCallback(
    (selection: Selection) => {
      const [newValue] = selection;
      const preset = presets.find(p => p.label === newValue);
      if (!preset) return;

      const start = typeof preset.startDate === "function" ? preset.startDate() : preset.startDate;
      const end = typeof preset.endDate === "function" ? preset.endDate() : preset.endDate;
      state.setValue({ start, end });
      if (!manualValidation) state.setOpen(false);
      onSelectionChange?.(preset, start, end);
    },
    [manualValidation, onSelectionChange, presets, state],
  );

  return (
    <ListBox
      ref={listRef}
      aria-label="presets"
      items={options}
      className={PresetsWrapper}
      selectionMode="single"
      selectionBehavior="replace"
      selectedKeys={selected ?? []}
      onSelectionChange={onSelectPreset}
    >
      {item => (
        <ListBoxItem
          key={item.id}
          textValue={item.name}
          className={classNames(StyledListBoxItem, ItemClassname)}
        >
          <Text type="body2">{item.name}</Text>
        </ListBoxItem>
      )}
    </ListBox>
  );
};

export default PresetSelector;

{
  /* <PresetsWrapper>
  {presets.map(preset => (
    <StyledButton
      key={preset.label}
      onClick={() => handleSelect(preset)}
      className={isPressetSelected(preset) ? "current" : undefined}
    >
      {preset.label}
    </StyledButton>
  ))}
</PresetsWrapper> */
}
